import { isNil } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getController, setControlledTimeout } from '@shared/util/timeout';

// how long core-api's search index can take to reflect a change after its event goes out
const INDEX_DELAY_MS = 2500;
// each client adds a random share of this, so the clients that heard the same change spread out
const STAGGER_MS = 3000;
// a refresh runs no sooner than this after the last one, so a burst of changes costs one
const MIN_INTERVAL_MS = 10000;
// a refresh waits no longer than this, from its request, for the list to stop changing
const MAX_WAIT_MS = 30000;

interface StaggeredRefreshOptions<Key> {
  // what the refresh is for, such as a query's variables; a request made for another key than the
  // current one is dropped, as whatever replaced the key fetches fresh anyway
  key: Key;
  // holds a requested refresh back until it's false again
  isHeld?: boolean;
  // when what the refresh reads last changed here: the refresh waits until the search index has
  // had time to take that change in too, or it would read a result without it
  getLastChangeAt?: () => number | null;
}

/**
 * Runs a query that a change made elsewhere calls for, staggered so the clients that all heard
 * the same change don't send it at the same moment. It waits for the search index to take in the
 * change plus a random share of the stagger, at least the minimum interval since it last ran, and
 * until the index has had time for the latest change too - but no longer than the longest wait,
 * or a list that keeps changing would never refresh. A hidden tab waits until it's shown again. A
 * request made while one is waiting joins it.
 */
export function useStaggeredRefresh<Key>(
  refresh: (key: Key) => void,
  { key, isHeld = false, getLastChangeAt }: StaggeredRefreshOptions<Key>,
): VoidFunction {
  const refreshRef = useRef(refresh);
  const getLastChangeAtRef = useRef(getLastChangeAt);
  useEffect(() => {
    refreshRef.current = refresh;
    getLastChangeAtRef.current = getLastChangeAt;
  });
  const lastRunAtRef = useRef<number | null>(null);
  const [requested, setRequested] = useState<{ key: Key; requestedAt: number } | null>(null);

  useEffect(() => {
    if (isNil(requested) || requested.key !== key || isHeld) {
      return;
    }
    const sinceLastRun = isNil(lastRunAtRef.current) ? Infinity : Date.now() - lastRunAtRef.current;
    const delay = Math.max(
      INDEX_DELAY_MS + Math.random() * STAGGER_MS,
      MIN_INTERVAL_MS - sinceLastRun,
    );
    const { signal, abort } = getController();
    const run = () => {
      // Read when it's time to run rather than kept in state, so a tab switch doesn't re-render
      // every list, and a list only listens for one while it has a refresh waiting.
      if (isDocumentHidden()) {
        document.addEventListener('visibilitychange', run, { signal, once: true });
        return;
      }
      const lastChangeAt = getLastChangeAtRef.current?.();
      const sinceLastChange = isNil(lastChangeAt) ? Infinity : Date.now() - lastChangeAt;
      const hasWaitedLongest = Date.now() - requested.requestedAt >= MAX_WAIT_MS;
      if (sinceLastChange < INDEX_DELAY_MS && !hasWaitedLongest) {
        setControlledTimeout(run, INDEX_DELAY_MS - sinceLastChange, signal);
        return;
      }
      lastRunAtRef.current = Date.now();
      setRequested(null);
      refreshRef.current(key);
    };
    setControlledTimeout(run, delay, signal);
    return abort;
  }, [isHeld, key, requested]);

  return useCallback(
    () =>
      setRequested(current => (current?.key === key ? current : { key, requestedAt: Date.now() })),
    [key],
  );
}

function isDocumentHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}
